import React, { useEffect, useState } from 'react';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import moment from 'moment';
import LandmanPayExceptionHistory from 'src/models/personnel/LandmanPayExceptionHistory';
import LandmanPayException from 'src/models/personnel/LandmanPayException';
import useFetch from 'use-http';
import PersonnelToast from '../../components/PersonnelToast';
import TSDILandman from 'src/models/personnel/TSDILandman';

interface IProjectPayExceptionHistoryDialogProps {
    dialogName: string;
    payExceptions: Array<LandmanPayException>;
    hideDialog: () => void;
    selectedPersonnel: TSDILandman;
}

const ProjectPayExceptionHistoryDialog = (props: IProjectPayExceptionHistoryDialogProps) => {
    const { get, response } = useFetch();
    const [toast, setToast] = useState<{ message: string; color?: string, persistent?: boolean; } | null>(null);
    const [projectPayExceptions, setProjectPayExceptions] = useState<Array<LandmanPayExceptionHistory>>();
    const [projectName, setProjectName] = useState<string>();
    const [selectedClNo, setSelectedClNo] = useState<number>();
    const [selectedPrNo, setSelectedPrNo] = useState<number>();

    useEffect(() => {
        onCreate();
    }, []);

    const onCreate = () => {
        console.log('PayExceptionHistoryDialog onCreate()', props.payExceptions);
    };

    const OKClicked = () => {
        props.hideDialog();
    };

    // when current exception is clicked
    const getPayHistoryByProject = async (clientNumber: number, projectNumber: number) => {
        if (props?.selectedPersonnel?.lmNo !== undefined && props?.selectedPersonnel?.lmNo !== null && props?.selectedPersonnel?.lmNo > 0) {
            let data = await get(`v1.0/landmen/personnel/${props?.selectedPersonnel?.lmNo}/clients/${clientNumber}/projects/${projectNumber}/get-project-pay-exceptions`);

            if (response.ok) {
                setProjectPayExceptions(data);
            } else {
                showToast('Unable to get selected personnel\'s pay history information for this project', `rgba(255,0,0,0.5)`);
            }
        } else {
            showToast('Invalid employee number provided: ' + props?.selectedPersonnel?.lmNo, `rgba(255,0,0,0.5)`);
        }
    };

    const getPayByValue = (value: string) => {
        switch (value) {
            case 'D':
                return 'Day';
            case 'H':
                return 'Hour';
            case 'M':
                return 'Month';
            default:
                return 'Unknown';
        }
    };

    const setSelectedRow = async (rowInfo: LandmanPayException) => {
        setSelectedClNo(rowInfo.clNo);
        setSelectedPrNo(rowInfo.prNo);
        setProjectName(rowInfo.projectName);
        getPayHistoryByProject(rowInfo.clNo, rowInfo.prNo);
    };

    const showToast = (message: string, color?: string, persistent?: boolean) => {
        setToast({ message, persistent, color });
    };

    return (
        <Dialog title="Project Pay Exception History" dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ maxHeight: '75%', width: '65%' }}>
            <div>
                {toast && (
                    <PersonnelToast
                        message={toast.message}
                        color={toast.color}
                        onClose={() => setToast(null)}
                        persistent={toast.persistent}
                    />
                )}
            </div>
            <h3>Pay Exceptions</h3>
            {props.payExceptions?.length > 0 && <div style={{ overflowY: 'scroll', maxHeight: '35rem' }}>
                <table style={{ width: '100%' }}>
                    <thead>
                        <tr style={{ background: '#d6d6d6' }}>
                            <th style={{ width: '5%' }}>Client</th>
                            <th style={{ width: '20%' }}>Client Name</th>
                            <th style={{ width: '5%' }}>Project</th>
                            <th style={{ width: '20%' }}>Project Name</th>
                            <th style={{ width: '10%' }}>Pay Rate</th>
                            <th style={{ width: '5%' }}>Pay Unit</th>
                            <th style={{ width: '10%' }}>Classification</th>
                        </tr>
                    </thead>
                    {props.payExceptions?.map((history: LandmanPayException, index: number) => {
                        return (
                            <tbody key={`body-${index}`}>
                                <tr 
                                    key={`p-${index}`} 
                                    onClick={() => setSelectedRow(history)}
                                    style={{ width: '100%', backgroundColor: (selectedClNo === history?.clNo && selectedPrNo === history?.prNo) ? 'yellow' : undefined }}
                                >
                                    <td style={{ width: '5%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                        {history?.clNo}
                                    </td>
                                    <td style={{ width: '20%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                        {history?.clientName}
                                    </td>
                                    <td style={{ width: '5%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                        {history?.prNo}
                                    </td>
                                    <td style={{ width: '20%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                        {history?.projectName}
                                    </td>
                                    <td style={{ width: '10%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                        {parseFloat(history?.payRate?.toString()).toFixed(2)}
                                    </td>
                                    <td style={{ width: '5%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                        {getPayByValue(history?.payUnit)}
                                    </td>
                                    <td style={{ width: '10%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                        TBD
                                    </td>
                                </tr>
                            </tbody>
                        );
                    })}
                </table>
            </div>}
            <br />
            <hr />
            <h3>Pay Exceptions History Detail {projectName ? `for ${projectName}` : ''}</h3>
            {projectPayExceptions?.length > 0 && <div>
                <div style={{ overflowY: 'scroll', maxHeight: '25rem' }}>
                    <table style={{ width: '100%' }}>
                        <thead>
                            <tr style={{ background: '#d6d6d6' }}>
                                <th style={{ width: '10%' }}>Pay By</th>
                                <th style={{ width: '10%' }}>Pay Rate</th>
                                <th style={{ width: '15%' }}>Eff Date</th>
                                <th style={{ width: '10%' }}>Manager</th>
                                <th style={{ width: '25%' }}>Manager Name</th>
                                <th style={{ width: '10%' }}>Prod. Bonus</th>
                                <th style={{ width: '10%' }}>Prev Pay By</th>
                                <th style={{ width: '10%' }}>Prev Pay Rate</th>
                            </tr>
                        </thead>
                        {projectPayExceptions?.map((pay: LandmanPayExceptionHistory, index: number) => {
                            return (
                                <tbody key={index}>
                                    <tr className={`contract-pay-history-active-item-${index}`}>
                                        <td style={{ width: '10%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {getPayByValue(pay.payUnit)}
                                        </td>
                                        <td style={{ width: '10%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {parseFloat(pay.payRate?.toString()).toFixed(2)}
                                        </td>
                                        <td style={{ width: '15%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {moment(pay.chgDate).format('YYYY-MM-DD') as unknown as Date}
                                        </td>
                                        <td style={{ width: '10%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {pay.mgrNo}
                                        </td>
                                        <td style={{ width: '25%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {pay.tsdUser}
                                        </td>
                                        <td style={{ width: '10%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            ?
                                        </td>
                                        <td style={{ width: '10%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {getPayByValue(pay.prevUnit)}
                                        </td>
                                        <td style={{ width: '15%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {parseFloat(pay.prevRate?.toString()).toFixed(2)}
                                        </td>
                                    </tr>
                                </tbody>
                            );
                        })}
                    </table>
                </div>
            </div>}
            {props.payExceptions?.length <= 0 && <div className="bold-centered">No Pay Exceptions for Project</div>}
            <div className="form-row" style={{ marginTop: '2rem', justifyContent: 'center', textAlign: 'center' }}>
                <Button
                    className="blue-hover-button"
                    style={{ width: '24%', marginRight: '1rem' }}
                    text={'Close'}
                    onClick={() => OKClicked()}
                />
            </div>
        </Dialog>
    );
};

export default ProjectPayExceptionHistoryDialog;



// WEBPACK FOOTER //
// ./src/components/personnel/dialogs/pay/ProjectPayExceptionAndHistoryDialog.tsx